/**
 * Hash-based client-side router for SnapHub PWA.
 *
 * Supports:
 * - Route registration with path, lazy loader, layout type, and auth flag
 * - Hash-change navigation (`hashchange` event)
 * - Two layout types: 'sidebar' and 'fullscreen'
 * - Lazy loading via dynamic import() for each page module
 * - Programmatic navigation via navigate()
 * - Auth guard integration point (isAuthenticated callback)
 * - Sidebar navigation rendering after layout change
 *
 * Validates: Requirements 40.1, 45.2
 */

import { setTitle } from '../shared/services/page-title.service';
import { requiresCustomerSelection, requiresCustomerId } from './customer-guard';
import { showCustomerRequiredOverlay, hideCustomerRequiredOverlay } from '../shared/components/customer-required-overlay';
import { showCustomerIdPendingOverlay, hideCustomerIdPendingOverlay } from '../shared/components/customerid-pending-overlay';
import { storageAdapter } from './storage-adapter';
import { decodeTokenPayload, resolveRoleFromPayload } from '../features/auth/auth.service';
import { canAccessConfigurator } from '../features/configurator/configurator-feature.guard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LayoutType = 'sidebar' | 'fullscreen';

export interface PageModule {
  render: (container: HTMLElement) => void | Promise<void>;
  cleanup?: () => void;
}

export interface RouteDefinition {
  path: string;
  loader: () => Promise<PageModule>;
  layout: LayoutType;
  requiresAuth: boolean;
  title?: string;
  /** Optional role restriction. When set, only users with one of these roles can access the route. */
  allowedRoles?: string[];
}

export interface RouterOptions {
  /** Root element where pages are rendered */
  outlet: HTMLElement;
  /** Callback that returns true when the user is authenticated */
  isAuthenticated: () => boolean;
  /** Called when an unauthenticated user hits a protected route */
  onUnauthenticated?: () => void;
}

// ---------------------------------------------------------------------------
// Router state (module-level singleton)
// ---------------------------------------------------------------------------

let routes: RouteDefinition[] = [];
let options: RouterOptions | null = null;
let started = false;
let activePageModule: PageModule | null = null;

// ---------------------------------------------------------------------------
// Sidebar navigation callback
// ---------------------------------------------------------------------------

/** Optional callback to render sidebar navigation after layout change. */
let onSidebarReady: ((sidebarEl: HTMLElement, mainEl: HTMLElement) => void) | null = null;

// ---------------------------------------------------------------------------
// Page header callback
// ---------------------------------------------------------------------------

/** Optional callback to render the page header after layout change. */
let onPageHeaderReady: ((mainEl: HTMLElement) => void) | null = null;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Register a single route.
 */
export function registerRoute(route: RouteDefinition): void {
  routes.push(route);
}

/**
 * Register multiple routes at once.
 */
export function registerRoutes(defs: RouteDefinition[]): void {
  defs.forEach((r) => routes.push(r));
}

/**
 * Initialise the router and start listening for hash changes.
 * Call this once after all routes have been registered.
 */
export function initRouter(opts: RouterOptions): void {
  options = opts;

  window.addEventListener('hashchange', () => {
    void handleRouteChange();
  });

  started = true;

  // Render the initial route
  void handleRouteChange();
}

/**
 * Navigate programmatically by updating the hash.
 */
export function navigate(path: string): void {
  window.location.hash = `#${path}`;
}

/**
 * Return the current route path derived from `location.hash`.
 * Falls back to `/` when the hash is empty.
 * Strips query parameters (everything after `?`) for route matching.
 */
export function getCurrentRoute(): string {
  const hash = window.location.hash.replace(/^#/, '');
  // Strip query parameters for route matching
  const path = hash.split('?')[0];
  return path || '/';
}

/**
 * Return the RouteDefinition that matches the current hash, or undefined.
 */
export function getMatchedRoute(): RouteDefinition | undefined {
  const current = getCurrentRoute();
  return routes.find((r) => r.path === current);
}

/**
 * Tear down the router (useful for tests).
 */
export function destroyRouter(): void {
  if (activePageModule?.cleanup) {
    activePageModule.cleanup();
  }
  activePageModule = null;
  routes = [];
  options = null;
  started = false;
  onSidebarReady = null;
  onPageHeaderReady = null;
}

/**
 * Register a callback that is invoked whenever a sidebar layout is applied.
 * The callback receives the sidebar element and the main content element.
 */
export function setOnSidebarReady(
  callback: ((sidebarEl: HTMLElement, mainEl: HTMLElement) => void) | null,
): void {
  onSidebarReady = callback;
}

/**
 * Register a callback that is invoked after layout is applied to render the page header.
 * The callback receives the main content element.
 */
export function setOnPageHeaderReady(
  callback: ((mainEl: HTMLElement) => void) | null,
): void {
  onPageHeaderReady = callback;
}

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

function applySidebarLayout(outlet: HTMLElement): HTMLElement {
  outlet.innerHTML = '';

  const wrapper = document.createElement('div');
  wrapper.className = 'layout-sidebar';

  const sidebar = document.createElement('aside');
  sidebar.className = 'sidebar';
  sidebar.id = 'sidebar';
  // Sidebar content will be populated by the sidebar-navigation component (Task 6.1)

  const main = document.createElement('main');
  main.className = 'main-content';
  main.id = 'page-content';

  // Header area – rendered by page-header component, survives page re-renders
  const headerArea = document.createElement('div');
  headerArea.id = 'page-header-area';

  // Content area – pages render into this container
  const contentArea = document.createElement('div');
  contentArea.className = 'main-content__body';
  contentArea.id = 'page-content-body';

  main.appendChild(headerArea);
  main.appendChild(contentArea);

  wrapper.appendChild(sidebar);
  wrapper.appendChild(main);
  outlet.appendChild(wrapper);

  return contentArea;
}

function applyFullscreenLayout(outlet: HTMLElement): HTMLElement {
  outlet.innerHTML = '';

  const main = document.createElement('main');
  main.className = 'layout-fullscreen';
  main.id = 'page-content';

  outlet.appendChild(main);

  return main;
}

// ---------------------------------------------------------------------------
// Internal route handling
// ---------------------------------------------------------------------------

async function handleRouteChange(): Promise<void> {
  if (!options || !started) return;

  const currentPath = getCurrentRoute();
  const route = routes.find((r) => r.path === currentPath);

  if (!route) {
    // Unknown route – fall back to `/`
    navigate('/');
    return;
  }

  // Auth guard integration point
  if (route.requiresAuth && !options.isAuthenticated()) {
    if (options.onUnauthenticated) {
      options.onUnauthenticated();
    } else {
      navigate('/login');
    }
    return;
  }

  // Role guard: restrict routes to specific roles when allowedRoles is defined
  if (route.allowedRoles && route.allowedRoles.length > 0) {
    const token = storageAdapter.getAccessToken();
    const payload = token ? decodeTokenPayload(token) : null;
    const userRole = resolveRoleFromPayload(payload);
    if (!route.allowedRoles.includes(userRole)) {
      navigate('/');
      return;
    }
  }

  // Configurator guard: restrict /configurator/* routes to whitelisted users
  if (currentPath.startsWith('/configurator') && !canAccessConfigurator()) {
    navigate('/');
    return;
  }

  // Clean up the previous page module if it has a cleanup function
  if (activePageModule?.cleanup) {
    activePageModule.cleanup();
  }
  activePageModule = null;

  // Apply the correct layout and get the content container
  const contentContainer =
    route.layout === 'fullscreen'
      ? applyFullscreenLayout(options.outlet)
      : applySidebarLayout(options.outlet);

  // Set the page title from the route definition
  if (route.title) {
    setTitle(route.title);
  }

  // If sidebar layout, notify the sidebar navigation callback
  if (route.layout === 'sidebar' && onSidebarReady) {
    const sidebarEl = document.getElementById('sidebar');
    if (sidebarEl) {
      onSidebarReady(sidebarEl, contentContainer);
    }
  }

  // Render the page header (for sidebar layouts)
  if (route.layout === 'sidebar' && onPageHeaderReady) {
    const headerArea = document.getElementById('page-header-area');
    if (headerArea) {
      onPageHeaderReady(headerArea);
    }
  }

  // CustomerId pending guard: if user has no customerId (e.g. new SSO user), show info overlay
  if (requiresCustomerId(currentPath)) {
    hideCustomerRequiredOverlay();
    showCustomerIdPendingOverlay(contentContainer);
    return;
  } else {
    hideCustomerIdPendingOverlay();
  }

  // Customer guard: if employee/admin has no customer selected, show overlay instead of page
  if (requiresCustomerSelection(currentPath)) {
    showCustomerRequiredOverlay(contentContainer);
    return;
  } else {
    hideCustomerRequiredOverlay();
  }

  // Lazy-load the page module and render
  try {
    const pageModule = await route.loader();
    activePageModule = pageModule;
    await pageModule.render(contentContainer);
  } catch (err) {
    console.error('[Router] Page load/render failed:', err);
    // Dynamic import failures after a deployment are typically caused by stale
    // chunk hashes. Reload once to pick up the new asset manifest.
    const isChunkError =
      err instanceof TypeError ||
      (err instanceof Error &&
        (/loading.*chunk|failed to fetch|dynamically imported module|load module script/i.test(err.message)));

    const RELOAD_KEY = 'snaphub_chunk_reload';
    const lastReload = sessionStorage.getItem(RELOAD_KEY);
    const now = Date.now();

    if (isChunkError && (!lastReload || now - Number(lastReload) > 10_000)) {
      // Mark this reload so we don't loop endlessly
      sessionStorage.setItem(RELOAD_KEY, String(now));
      window.location.reload();
      return;
    }

    activePageModule = null;
    contentContainer.innerHTML = `<p class="text-error p-md">Seite konnte nicht geladen werden.</p>`;
  }
}
